import React from 'react';
import type { CampaignStatus } from '../../types/campaign';

interface BulkActionBarProps {
  selectedCount: number;
  onStatusChange: (status: CampaignStatus) => void;
  onAssignManager: () => void;
  onApplyTags: () => void;
  onDelete: () => void;
  onClear: () => void;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onStatusChange,
  onAssignManager,
  onApplyTags,
  onDelete,
  onClear,
}) => {
  return (
    <div className="sticky top-16 z-40 md:relative md:top-0 bg-indigo-50 border-l-4 border-indigo-500 p-3 md:p-4 rounded-md mb-4 shadow-md md:shadow-none">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <div className="flex items-center gap-2 flex-shrink-0">
          <svg
            className="w-5 h-5 text-indigo-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium text-indigo-900 text-sm md:text-base">
            {selectedCount} campaign{selectedCount !== 1 ? 's' : ''} selected
          </span>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 scrollbar-thin">
          {/* Status Change Dropdown */}
          <div className="relative group flex-shrink-0">
            <button className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap touch-manipulation">
              <span className="hidden sm:inline">Change Status</span>
              <span className="sm:hidden">Status</span>
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div className="hidden group-hover:block absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1">
                <button
                  onClick={() => onStatusChange('active')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 touch-manipulation"
                >
                  Set to Active
                </button>
                <button
                  onClick={() => onStatusChange('paused')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 touch-manipulation"
                >
                  Set to Paused
                </button>
                <button
                  onClick={() => onStatusChange('archived')}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 touch-manipulation"
                >
                  Set to Archived
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={onAssignManager}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap touch-manipulation flex-shrink-0"
          >
            <span className="hidden sm:inline">Assign Manager</span>
            <span className="sm:hidden">Manager</span>
          </button>

          <button
            onClick={onApplyTags}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap touch-manipulation flex-shrink-0"
          >
            <span className="hidden sm:inline">Apply Tags</span>
            <span className="sm:hidden">Tags</span>
          </button>

          <button
            onClick={onDelete}
            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 whitespace-nowrap touch-manipulation flex-shrink-0"
          >
            Delete
          </button>

          <button
            onClick={onClear}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap touch-manipulation flex-shrink-0"
          >
            <span className="hidden sm:inline">Clear Selection</span>
            <span className="sm:hidden">Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
};

BulkActionBar.displayName = 'BulkActionBar';
